/**
 * API do fluxo BPe (passagens rodoviárias) no Chat.
 * Todas as rotas exigem permissão bpe.view e header Company.
 */

import { fetchData } from "./client";

/**
 * Lista cidades para origem ou destino.
 * @param {string} tipo - "origem" | "destino"
 * @param {number|null} origemId - para tipo=destino, id da cidade de origem para filtrar destinos
 */
export async function getBpeCidades(tipo = "origem", origemId = null) {
  const params = new URLSearchParams({ tipo });
  if (tipo === "destino" && origemId) params.set("origem_id", origemId);
  const res = await fetchData(`/bpe/cidades?${params.toString()}`, "GET");
  return res?.data ?? res ?? [];
}

/**
 * Lista passagens (BPs) já emitidas na conversa.
 */
export async function getBpeList(conversationId) {
  const res = await fetchData(`/conversations/${conversationId}/bpe`, "GET");
  return res?.data ?? res ?? [];
}

/**
 * Sugestões inteligentes para o wizard (trecho, data, horário, passageiro, pagamento).
 */
export async function getBpeSuggestions(conversationId) {
  const res = await fetchData(`/conversations/${conversationId}/bpe/suggestions`, "GET");
  return res?.data ?? res ?? null;
}

/** Carrega o rascunho do wizard (estado salvo) para retomar a venda na solicitação. */
export async function getBpeDraft(conversationId, options = {}) {
  const qs = new URLSearchParams();
  if (options.phone) qs.set("phone", options.phone);
  if (options.cpf) qs.set("cpf", options.cpf);
  if (options.clientId) qs.set("clientId", String(options.clientId));
  const q = qs.toString();
  const res = await fetchData(
    `/conversations/${conversationId}/bpe/draft${q ? `?${q}` : ""}`,
    "GET"
  );
  const data = res?.data ?? res;
  return {
    draft: data?.draft ?? null,
    saleStatus: data?.saleStatus ?? null,
  };
}

/** Salva/atualiza o rascunho do wizard (autosave das escolhas). */
export async function saveBpeDraft(conversationId, draft) {
  const res = await fetchData(`/conversations/${conversationId}/bpe/draft`, "PUT", { draft });
  const data = res?.data ?? res;
  return data?.draft ?? null;
}

/** Limpa o rascunho do wizard (venda concluída ou cancelada). */
export async function clearBpeDraft(conversationId) {
  try {
    await fetchData(`/conversations/${conversationId}/bpe/draft`, "DELETE");
  } catch (_) {
    /* limpeza best-effort */
  }
  return true;
}

/**
 * Consulta viagens disponíveis (origem, destino, data).
 * Body: { origem, destino, data_viagem, quantidade_passageiros?, id_formadepagamento? }
 * origem/destino podem ser postback string (##CIDADE##...) ou objeto { id, id_interno, nome, ... }
 */
export async function consultarViagens(conversationId, body) {
  const res = await fetchData(
    `/conversations/${conversationId}/bpe/consultar-viagens`,
    "POST",
    body
  );
  const data = res?.data ?? res;
  if (data?.error) throw new Error(data.error);
  const viagens = Array.isArray(data?.viagens)
    ? data.viagens
    : Array.isArray(data?.data?.viagens)
      ? data.data.viagens
      : [];
  const _debug = data?._debug ?? data?.data?._debug;
  const aviso = data?.aviso ?? data?.data?.aviso ?? null;
  return { ...data, viagens, aviso, _debug };
}

/**
 * Obtém mapa de assentos para a viagem selecionada.
 * Body: { opcao_viagem } (1-based)
 */
export async function getAssentos(conversationId, opcaoViagem, options = {}) {
  const body = { opcao_viagem: opcaoViagem };
  if (options.perna != null) body.perna = options.perna;
  if (options.conexao_index != null) body.conexao_index = options.conexao_index;
  const res = await fetchData(`/conversations/${conversationId}/bpe/assentos`, "POST", body);
  const data = res?.data ?? res;
  if (data?.error) throw new Error(data.error);
  return data;
}

/**
 * Pré-reserva uma poltrona (bloqueio).
 * Body: { poltrona, nome, documento, email?, celular?, index?, leg_index?,
 *   categoria_passageiro?, declaracao_documentacao_beneficio?,
 *   data_nascimento_gratuidade?, renda_mensal_gratuidade? }
 */
export async function preReserva(conversationId, body) {
  const res = await fetchData(`/conversations/${conversationId}/bpe/pre-reserva`, "POST", body);
  const data = res?.data ?? res;
  if (data?.error) throw new Error(data.error);
  return data;
}

/** Categorias de passageiro (benefícios) para o trecho atual. */
export async function getBpePassengerCategories(conversationId) {
  const res = await fetchData(`/conversations/${conversationId}/bpe/passenger-categories`, "GET");
  const data = res?.data ?? res;
  if (data?.error) throw new Error(data.error);
  return data;
}

/** Cota valor de gratuidade/benefício (integradores Luna/Rodosoft). */
export async function cotarGratuidadeBpe(conversationId, body) {
  const res = await fetchData(`/conversations/${conversationId}/bpe/cotar-gratuidade`, "POST", body);
  const data = res?.data ?? res;
  if (data?.error) throw new Error(data.error);
  return data;
}

/**
 * Confirma reserva e emite BPe.
 * Body: { passageiro_index?, nome?, documento?, email?, celular?, cidade?, cep?, endereco? }
 */
export async function confirmarReserva(conversationId, body) {
  const res = await fetchData(`/conversations/${conversationId}/bpe/reserva`, "POST", body);
  const data = res?.data ?? res;
  if (data?.error) throw new Error(data.error);
  return data;
}

/** Formas de pagamento habilitadas para BPe (emitirbpe=1). */
export async function getBpeFormasPagamento() {
  const res = await fetchData("/bpe/formas-pagamento", "GET");
  return res?.data ?? res ?? [];
}

/** Consulta multa / regras antes de cancelar. */
export async function consultarCancelamentoBpe(conversationId, body) {
  const res = await fetchData(
    `/conversations/${conversationId}/bpe/consultar-cancelamento`,
    "POST",
    body
  );
  const data = res?.data ?? res;
  if (data?.error) throw new Error(data.error);
  return data;
}

/** Cancela passagem emitida (opcional estorno automático). */
export async function cancelarBpe(conversationId, body) {
  const res = await fetchData(`/conversations/${conversationId}/bpe/cancelar`, "POST", {
    estornar: true,
    ...body,
  });
  const data = res?.data ?? res;
  if (data?.error) throw new Error(data.error);
  return data;
}

/** Estorna pagamento após cancelamento do BPe. */
export async function estornarBpePagamento(conversationId, body) {
  const res = await fetchData(`/conversations/${conversationId}/bpe/estornar`, "POST", body);
  const data = res?.data ?? res;
  if (data?.error) throw new Error(data.error);
  return data;
}

/** Jornada de compra (funil + auditoria + BPe). */
export async function getBpePurchaseJourney(conversationId) {
  const res = await fetchData(`/conversations/${conversationId}/bpe/journey`, "GET");
  return res?.data ?? res ?? { events: [], funnel: [], bpes: [] };
}

/** Status resumido da venda em andamento. */
export async function getBpeSaleStatus(conversationId) {
  const res = await fetchData(`/conversations/${conversationId}/bpe/sale-status`, "GET");
  return res?.data ?? res ?? null;
}

/** Inicia cobrança Pix / link / cartão (após pré-reserva). */
export async function chargeBpePayment(conversationId, body) {
  const res = await fetchData(`/conversations/${conversationId}/bpe/payments/charge`, "POST", body);
  const data = res?.data ?? res;
  if (data?.error) throw new Error(data.error);
  return data;
}

/** Consulta status do pagamento (polling). */
export async function getBpePaymentStatus(conversationId, params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== "") qs.set(k, String(v));
  });
  const q = qs.toString();
  const res = await fetchData(
    `/conversations/${conversationId}/bpe/payments/status${q ? `?${q}` : ""}`,
    "GET"
  );
  return res?.data ?? res;
}

/** Emite todos os BPe pendentes após pagamento confirmado. */
export async function emitirBpeAposPagamento(conversationId, body) {
  const res = await fetchData(`/conversations/${conversationId}/bpe/reserva`, "POST", {
    ...body,
    emit_pending: true,
  });
  const data = res?.data ?? res;
  if (data?.error) throw new Error(data.error);
  return data;
}

export const BPE_PAYMENT_TIPO = {
  CASH: 5,
  PIX_ITAU: 8,
  REDE: 9,
  CIELO: 3,
  MERCADO_PAGO: 6,
  XPAY: 7,
  APPMAX: 11,
};

/**
 * Emissão imediata (sem confirmação de pagamento) é permitida APENAS para dinheiro.
 * Qualquer outra forma exige a confirmação do pagamento (gateway) antes de emitir o BPe.
 */
export function isImmediateBpePayment(pm) {
  return Number(pm?.tipo) === BPE_PAYMENT_TIPO.CASH;
}

export function needsOnlineBpeCharge(pm) {
  const t = Number(pm?.tipo);
  return [3, 6, 7, 8, 11].includes(t) || t === BPE_PAYMENT_TIPO.REDE;
}

export function isRedeCardPayment(pm) {
  return Number(pm?.tipo) === BPE_PAYMENT_TIPO.REDE;
}

export function isPixOrLinkPayment(pm) {
  const t = Number(pm?.tipo);
  return t === BPE_PAYMENT_TIPO.PIX_ITAU || [3, 6, 7, 11].includes(t);
}

export function mapBpeAutoToResults(bpeAuto) {
  const list = bpeAuto?.emitted || bpeAuto?.results || [];
  if (!Array.isArray(list)) return [];
  return list
    .filter((e) => e && e.ok !== false && !e.error)
    .map((e) => ({
      dados_bpe: e.dados_bpe,
      reservationId: e.dados_bpe?.Localizador || e.reservationId,
      bpe: e.bpe,
      legIndex: e.legIndex != null ? Number(e.legIndex) : null,
      legTipo: e.legTipo || null,
    }));
}

/**
 * Extrai informações de falha de emissão de um payload bpeAuto (emissão pós-pagamento).
 * Retorna null quando tudo foi emitido com sucesso.
 */
export function extractBpeEmissionFailure(bpeAuto) {
  if (!bpeAuto) return null;
  const list = bpeAuto.emitted || bpeAuto.results || [];
  const failed = Array.isArray(list) ? list.filter((e) => e && (e.ok === false || e.error)) : [];
  if (!bpeAuto.anyFailed && !failed.length) return null;
  return {
    failedCount: failed.length,
    messages: failed.map((e) => e.error).filter(Boolean),
    delivery: bpeAuto.delivery || null,
  };
}
